/** @jsx jsx */
import { ThemeProvider, css, jsx } from '@emotion/react';

import * as React from 'react';

import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToStores from 'js/lib/connectToStores';
import { useLocation } from 'js/lib/useRouter';

import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import SimplifiedHeader from 'bundles/composable-page-header-consumer/header-renderers/SimplifiedHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import CourseraPlusHubPageMetatags from 'bundles/coursera-plus-landing-page/components/CourseraPlusHubPageMetatags';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import { searchTheme } from 'bundles/search-common/searchTheme';
import ApplicationStoreClass from 'bundles/ssr/stores/ApplicationStore';

import 'css!./styl/index';

const PAGE_HEADER_CLASSNAME_OVERRIDE = 'page-header-classname-override';

const styles = {
  container: (isMarketing: boolean) => css`
    @media (max-width: 607px) {
      padding-bottom: 52px;
    }

    // This is a hack to override the page header background color for the Coursera Plus landing page.
    .${PAGE_HEADER_CLASSNAME_OVERRIDE} {
      background-color: var(--cds-color-blue-25);
    }

    ${isMarketing
      ? css`
          .height-placeholder.with-desktop-header-controls {
            height: 33px;
          }
        `
      : ''}
  `,
};

type PropsFromStores = {
  csrfToken: string;
  requestCountryCode: string;
};

const CourseraPlusHubApp = (props: React.PropsWithChildren<PropsFromStores>) => {
  const { children } = props;
  const { pathname } = useLocation();
  const isMarketing = pathname.includes('subscribe') || pathname.includes('special');
  const shouldDisableIndexing = isMarketing;
  const shouldDisablePageHeaderControls = isMarketing;

  return (
    <ThemeProvider theme={searchTheme}>
      <div className="rc-CourseraPlusHubApp" css={styles.container(isMarketing)}>
        <CourseraPlusHubPageMetatags disableCrawlerIndexing={shouldDisableIndexing} />
        {shouldDisablePageHeaderControls ? (
          <SimplifiedHeader
            showProgramSwitcher={false}
            showLanguageDropdown={false}
            showAcctDropdown={false}
            showSignupBtn={false}
            showLoginBtn={false}
            showMobileHamburger={false}
          />
        ) : (
          <DefaultHeader showMetaNav isPositionStatic />
        )}
        <UniversalPageFeatures />
        <div role="main">{children}</div>
        <PageFooter />
      </div>
    </ThemeProvider>
  );
};

export default compose<PropsFromStores, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'premium_hub',
      page: 'coursera_plus_landing_page',
    },
  })),
  connectToStores<PropsFromStores, {}>([ApplicationStoreClass], (ApplicationStore, props) => ({
    ...props,
    csrfToken: ApplicationStore.getState().csrfToken,
    requestCountryCode: ApplicationStore.getState().requestCountryCode,
  }))
)(CourseraPlusHubApp);
